import {
  SENTINEL_STAGES,
  type SentinelStageId,
  type SentinelStageState,
  type SentinelStageStatuses,
  STAGE_LABELS,
} from 'features/sentinelDesign/stages'
import { FC } from 'react'
import styled, { keyframes } from 'styled-components'

const STATE_COLOR: Record<SentinelStageState, string> = {
  pending: '#94a3b8',
  running: '#38bdf8',
  done: '#22c55e',
  failed: '#ef4444',
  blocked: '#64748b',
}

const STATE_WORD: Record<SentinelStageState, string> = {
  pending: 'not started',
  running: 'running',
  done: 'done',
  failed: 'failed',
  blocked: 'waiting on an earlier stage',
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const Rail = styled.ol`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
`

const Step = styled.li`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
`

const Connector = styled.span`
  width: 1rem;
  height: 1px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.forecast.border};
`

const StepButton = styled.button<{
  $state: SentinelStageState
  $active: boolean
}>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $state, $active }) =>
      $active ? STATE_COLOR[$state] : theme.forecast.border};
  background: ${({ theme, $active }) =>
    $active ? theme.forecast.background2 : 'transparent'};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ $state }) => ($state === 'blocked' ? 0.55 : 1)};

  &:hover:not(:disabled) {
    border-color: ${({ $state }) => STATE_COLOR[$state]};
  }
`

const Dot = styled.span<{ $state: SentinelStageState }>`
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $state }) => STATE_COLOR[$state]};
`

const Spinner = styled.span`
  width: 0.6rem;
  height: 0.6rem;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid ${STATE_COLOR.running};
  border-top-color: transparent;
  animation: ${spin} 0.9s linear infinite;
`

const Label = styled.span`
  font-weight: 500;
`

const Summary = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 16rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export interface StageRailProps {
  statuses: SentinelStageStatuses
  active: SentinelStageId
  onSelect: (stage: SentinelStageId) => void
}

/**
 * The four stages across the top, each carrying its own state.
 *
 * The rail is the flow's table of contents, which is why it renders every stage
 * from the first paint rather than growing one control at a time: someone who
 * opens a run half-way through needs to see that a plan exists behind them, not
 * infer it from which buttons happen to be on screen.
 */
export const StageRail: FC<StageRailProps> = ({
  statuses,
  active,
  onSelect,
}) => (
  <Rail aria-label='Design stages'>
    {SENTINEL_STAGES.map((stage, index) => {
      const status = statuses[stage]
      const isActive = stage === active
      return (
        <Step key={stage}>
          {index > 0 && <Connector aria-hidden />}
          <StepButton
            type='button'
            $state={status.state}
            $active={isActive}
            aria-current={isActive ? 'step' : undefined}
            title={`${STAGE_LABELS[stage]} — ${STATE_WORD[status.state]}. ${status.summary}`}
            onClick={() => onSelect(stage)}>
            {status.state === 'running' ? (
              <Spinner aria-hidden />
            ) : (
              <Dot $state={status.state} aria-hidden />
            )}
            <Label>{STAGE_LABELS[stage]}</Label>
            <Summary>{status.summary}</Summary>
          </StepButton>
        </Step>
      )
    })}
  </Rail>
)
